import { useState, useEffect, useMemo, useCallback } from "react";
import { WhatsNewArticle } from "../types/whatsNew.types";
import { WhatsNewRepository } from "../repository/whatsNew.repo";

export const useWhatsNewData = () => {
  const [articles, setArticles] = useState<WhatsNewArticle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const repository = useMemo(() => new WhatsNewRepository(), []);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedArticles = await repository.fetchAllArticles();
      setArticles(fetchedArticles);
    } catch (err) {
      setError("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  }, [repository]);

  const createArticle = async (articleData: Omit<WhatsNewArticle, "docId">) => {
    try {
      await repository.createArticle(articleData);
      await fetchArticles();
    } catch (err) {
      throw new Error("Failed to create article");
    }
  };

  const updateArticle = async (
    documentId: string,
    articleData: Partial<WhatsNewArticle>
  ) => {
    try {
      await repository.updateArticle(documentId, articleData);
      await fetchArticles();
    } catch (err) {
      throw new Error("Failed to update article");
    }
  };

  const archiveArticle = async (
    documentId: string,
    shouldArchive: boolean
  ) => {
    try {
      await repository.archiveArticle(documentId, shouldArchive);
      await fetchArticles();
    } catch (err) {
      throw new Error("Failed to archive article");
    }
  };

  const updateArticleVisibility = async (
    documentId: string,
    visibility: boolean
  ) => {
    try {
      setArticles((prevArticles) =>
        prevArticles.map((article) =>
          article.docId === documentId ? { ...article, visibility } : article
        )
      );

      await repository.updateArticleVisibility(documentId, visibility);
    } catch (err) {
      setArticles((prevArticles) =>
        prevArticles.map((article) =>
          article.docId === documentId
            ? { ...article, visibility: !visibility }
            : article
        )
      );

      alert("Failed to update visibility. Please try again.");
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  return {
    articles,
    loading,
    error,
    fetchArticles,
    createArticle,
    updateArticle,
    archiveArticle,
    updateArticleVisibility,
  };
};
